import { useState } from "react";
import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  TrendingUp,
  Eye,
  ShoppingCart,
  Package,
  Star,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AnalyticsFreshnessBanner } from "@/components/admin/shared/AnalyticsFreshnessBanner";
import { analyticsApi } from "@/lib/admin-api/analytics";
import type { TrendingProduct } from "@/lib/admin-api/analytics";

// ---------------------------------------------------------------------------
// Period selector (hours-based)
// ---------------------------------------------------------------------------

type PeriodHours = 168 | 720 | 2160; // 7d, 30d, 90d

const PERIOD_OPTIONS: readonly { readonly value: PeriodHours; readonly label: string }[] = [
  { value: 168, label: "7 jours" },
  { value: 720, label: "30 jours" },
  { value: 2160, label: "90 jours" },
];

function PeriodSelector({
  value,
  onChange,
}: {
  readonly value: PeriodHours;
  readonly onChange: (p: PeriodHours) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {PERIOD_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            value === opt.value
              ? "bg-primary text-primary-foreground"
              : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

function formatNumber(n: number): string {
  return new Intl.NumberFormat("fr-FR").format(n);
}

// ---------------------------------------------------------------------------
// Score bar
// ---------------------------------------------------------------------------

function ScoreBar({ score, maxScore }: { readonly score: number; readonly maxScore: number }) {
  const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 rounded-full bg-muted/40 overflow-hidden">
        <div
          className="h-full rounded-full bg-amber-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-sm font-semibold text-foreground tabular-nums w-12 text-right">
        {formatNumber(score)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table skeleton row
// ---------------------------------------------------------------------------

function SkeletonRow() {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-6 py-3">
        <div className="h-4 w-6 animate-pulse rounded bg-muted" />
      </td>
      <td className="px-6 py-3">
        <div className="h-4 w-40 animate-pulse rounded bg-muted" />
      </td>
      <td className="px-6 py-3">
        <div className="h-4 w-12 ml-auto animate-pulse rounded bg-muted" />
      </td>
      <td className="px-6 py-3">
        <div className="h-4 w-12 ml-auto animate-pulse rounded bg-muted" />
      </td>
      <td className="px-6 py-3">
        <div className="h-4 w-12 ml-auto animate-pulse rounded bg-muted" />
      </td>
      <td className="px-6 py-3">
        <div className="h-4 w-full animate-pulse rounded bg-muted/40" />
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Trending() {
  const [period, setPeriod] = useState<PeriodHours>(720);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["analytics-trending", period],
    queryFn: () => analyticsApi.trending(period),
  });

  const trending: readonly TrendingProduct[] = data?.data?.trending ?? [];
  const maxScore = trending.length > 0 ? trending[0].score : 1;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <AnalyticsFreshnessBanner />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/analytics"
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              aria-label="Retour"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="font-display text-2xl font-bold text-foreground">
                Tendances produits
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Produits les plus populaires classes par score de tendance.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <PeriodSelector value={period} onChange={setPeriod} />
            <button
              type="button"
              onClick={() => refetch()}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              aria-label="Actualiser"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 text-sm text-destructive">
            Erreur lors du chargement des données. Veuillez réessayer.
          </div>
        )}

        {/* Table */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">#</th>
                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">Produit</th>
                  <th className="text-right px-6 py-3 font-medium text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <Eye className="h-3.5 w-3.5" /> Vues
                    </span>
                  </th>
                  <th className="text-right px-6 py-3 font-medium text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <ShoppingCart className="h-3.5 w-3.5" /> Ajouts panier
                    </span>
                  </th>
                  <th className="text-right px-6 py-3 font-medium text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <Package className="h-3.5 w-3.5" /> Achats
                    </span>
                  </th>
                  <th className="px-6 py-3 font-medium text-muted-foreground w-48">
                    <span className="inline-flex items-center gap-1.5">
                      <Star className="h-3.5 w-3.5" /> Score tendance
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 8 }, (_, i) => <SkeletonRow key={i} />)
                ) : trending.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                      Aucune donnee de tendance pour cette periode.
                    </td>
                  </tr>
                ) : (
                  trending.map((product, idx) => (
                    <tr
                      key={product.product_id}
                      className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                    >
                      <td className="px-6 py-3 text-muted-foreground tabular-nums">{idx + 1}</td>
                      <td className="px-6 py-3 font-medium text-foreground">
                        <span className="font-mono text-xs text-muted-foreground">
                          {product.product_id.slice(0, 8)}...
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right tabular-nums text-foreground">
                        {formatNumber(product.views)}
                      </td>
                      <td className="px-6 py-3 text-right tabular-nums text-foreground">
                        {formatNumber(product.cart_adds)}
                      </td>
                      <td className="px-6 py-3 text-right tabular-nums text-foreground">
                        {formatNumber(product.purchases)}
                      </td>
                      <td className="px-6 py-3">
                        <ScoreBar score={product.score} maxScore={maxScore} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
